import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { INDUSTRIES, CASE_STUDIES } from "../data/site";

const DETAILS = {
  "renewables": {
    body: "We work across wind, solar, storage, transmission and green hydrogen - where host community support determines whether projects are built on schedule or held up in objections. Our advice sharpens landholder engagement, cumulative impact response, and community benefit design.",
    focus: ["Landholder & neighbour engagement", "Transmission corridor community outcomes", "Cumulative impact across concurrent projects", "Community benefit programs and CBAs"],
  },
  "mining-resources": {
    body: "Critical minerals, coal transition, water access, Traditional Owner and Tribal relations. We advise operators, sponsors and lenders across the full project lifecycle - from options-stage social risk through to closure and just transition planning.",
    focus: ["Traditional Owner / Tribal engagement", "Workforce and accommodation modelling", "Water and land-use social risk", "Closure and post-mining transition"],
  },
  "infrastructure": {
    body: "Roads, rail, ports, water, utilities and long-linear transmission. These projects create corridor-scale social impacts and demand coordinated engagement across many communities and jurisdictions.",
    focus: ["Corridor-scale engagement design", "Land access and property acquisition impact", "Local content and workforce planning", "Multi-jurisdiction coordination"],
  },
  "government": {
    body: "Trusted advisor to public sector agencies leading assessment reform, regional transition programs, and community outcomes frameworks. Independent, evidence-based, and pragmatic about implementation.",
    focus: ["Policy and framework design", "Assessment reform advice", "Regional and just transition planning", "Independent expert input"],
  },
  "industrial-land": {
    body: "Large industrial, manufacturing and land development projects in populated regions - where community response is complex, layered and unforgiving. We help you shape design and engagement early enough to matter.",
    focus: ["Host community engagement strategy", "Cumulative impact in populated regions", "Workforce & housing pressure planning", "Long-run governance of community commitments"],
  },
};

export function IndustriesList() {
  return (
    <div data-testid="industries-page">
      <section className="bg-brand-blue text-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="eyebrow text-brand-yellow mb-6">Industries</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">Where our advice moves the needle.</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {INDUSTRIES.map((ind) => (
            <Link to={`/industries/${ind.slug}`} data-testid={`industry-tile-${ind.slug}`} key={ind.slug} className="group relative overflow-hidden h-80 block">
              <img src={ind.image} alt={ind.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue-deep via-brand-blue-deep/60 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 text-white">
                <h3 className="font-serif text-2xl mb-2 leading-snug">{ind.title}</h3>
                <p className="text-sm text-white/80">{ind.blurb}</p>
                <span className="mt-4 text-xs uppercase tracking-widest text-brand-yellow inline-flex items-center gap-2">Explore <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function IndustryDetail() {
  const { slug } = useParams();
  const industry = INDUSTRIES.find((i) => i.slug === slug);
  const detail = DETAILS[slug];
  const cases = CASE_STUDIES.filter((c) => c.industry === slug);

  if (!industry) return <div className="py-32 text-center"><h1 className="font-serif text-3xl text-brand-blue">Industry not found</h1></div>;

  return (
    <div data-testid={`industry-detail-${slug}`}>
      <section className="relative h-[60vh] flex items-end">
        <img src={industry.image} alt={industry.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue-deep via-brand-blue-deep/60 to-brand-blue-deep/20" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 text-white">
          <Link to="/industries" className="text-brand-yellow text-xs uppercase tracking-[0.2em] font-semibold hover:underline">← All industries</Link>
          <h1 className="font-serif text-4xl lg:text-6xl leading-tight mt-6 max-w-4xl">{industry.title}</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">The sector</div>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">{detail?.body}</p>
            <div className="eyebrow mb-4">Where we focus</div>
            <ul className="space-y-3">
              {detail?.focus.map((f) => (
                <li key={f} className="flex items-start gap-3 border-b border-slate-200 pb-3">
                  <span className="text-brand-orange font-serif font-bold">→</span>
                  <span className="text-slate-700">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-slate-50 border border-slate-200 p-8">
              <div className="eyebrow mb-4">Selected engagements</div>
              {cases.length === 0 && <p className="text-slate-500 text-sm">Case studies coming soon.</p>}
              <div className="space-y-4">
                {cases.map((c) => (
                  <Link to={`/case-studies/${c.slug}`} key={c.slug} className="block border border-slate-200 bg-white p-5 hover:border-brand-orange">
                    <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold mb-2">{c.outcomeTag}</div>
                    <div className="font-serif text-brand-blue">{c.title}</div>
                  </Link>
                ))}
              </div>
              <Link to="/contact" className="btn-primary w-full justify-center mt-6">Contact Us <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
